const states = new Map<
  string,
  {
    locked: boolean;
    result: unknown;
  }
>();

const hasValue = <T = unknown>(value: T) =>
  value !== undefined && value !== null;

/**
 * Executes a callback function with a resource lock to prevent concurrent executions.
 * Optionally caches the result to avoid redundant executions.
 * @function withLock
 * @template T - The type of the result.
 * @template D - The type of the default value.
 * @param {string} key - The key to identify the resource lock.
 * @param {() => Promise<T>} callback - The callback function to execute.
 * @param {object} [options] - The options for the resource lock.
 * @param {boolean} [options.cache=true] - Whether to cache the result.
 * @param {D} [options.defaultValue] - The default value to return in case of an error.
 * @returns {Promise<D extends undefined ? (T | undefined) : T>} The result of the callback function or the cached result.
 */
const withLock = async <T extends unknown, D extends T | undefined>(
  key: string,
  callback: () => Promise<T>,
  options?: {
    cache?: boolean;
    defaultValue?: D;
  },
): Promise<D extends undefined ? T | undefined : T> => {
  const hasCache = options?.cache !== false;

  let state = states.get(key);
  if (!state) {
    state = {
      locked: false,
      result: undefined,
    };

    states.set(key, state);
  }

  if (hasCache && hasValue(state.result)) return <T>state.result;

  if (state.locked) {
    return new Promise<T>((resolve) => {
      const execute = () => {
        if (state!.locked) {
          setTimeout(() => execute(), 50);
          return;
        }
        resolve(<T>state!.result);
        return;
      };
      setTimeout(() => execute(), 50);
    });
  }

  state.locked = true;

  try {
    state.result = await callback();
  } catch (e) {
    // console.log(e);

    state.result = options?.defaultValue;
  } finally {
    state.locked = false;
  }

  return <T>state.result;
};

export { withLock };
